import { ReversibleOperation } from './RoiMemento';

/** The baseline snapshot always lives at this index. See {@link UndoRedoManager}. */
export const BASELINE_STATE_INDEX = 0;

/**
 * The operation label a baseline capture carries.
 *
 * Defined here rather than alongside the service that writes it because the
 * manager needs it to tell an actual baseline from whatever merely happens to
 * be oldest. Position alone cannot: a stack that never captured a baseline
 * still has a state at index 0, and reading that as "the state before the
 * first edit" hands the user an ordinary edit under a label promising the
 * original.
 */
export const INITIAL_STATE_OPERATION = 'initial-state';

export interface UndoHistoryEntry {
    /** Position in the retained stack; pass to {@link UndoRedoManager.jumpToState}. */
    readonly index: number;
    readonly operation: ReversibleOperation;
    readonly isCurrent: boolean;
    /**
     * The baseline — the state captured before the first edit of the session.
     * False for every entry when the stack holds no baseline at all.
     */
    readonly isBaseline: boolean;
}

export class UndoRedoManager {
    private states: ReversibleOperation[] = [];
    private currentIndex = -1;
    private discardedIntermediateCount = 0;
    private listeners = new Set<() => void>();

    private notifyListeners(): void {
        this.listeners.forEach((listener) => {
            listener();
        });
    }

    constructor(private readonly maxStackSize = 10) {
        // Two slots is the floor at which a baseline and one edit can coexist,
        // which is the minimum for undo to mean anything. Below that,
        // `evictOldestIntermediate` would have to evict the baseline, and the
        // guarantee the whole class rests on would be silently untrue.
        if (!Number.isInteger(maxStackSize) || maxStackSize < 2) {
            throw new Error(
                `UndoRedoManager needs an integer maxStackSize of at least 2, got ${maxStackSize}`
            );
        }
    }

    /**
     * Subscribe to state changes
     * @param listener - Function called whenever undo/redo state changes
     * @returns Subscription object with unsubscribe method
     */
    subscribe(listener: () => void): { unsubscribe: () => void } {
        this.listeners.add(listener);
        listener();
        return {
            unsubscribe: () => {
                this.listeners.delete(listener);
            },
        };
    }

    /**
     * Add an operation to the history
     */
    addOperation(operation: ReversibleOperation): void {
        if (this.currentIndex < this.states.length - 1) {
            this.states = this.states.slice(0, this.currentIndex + 1);
        }
        this.states.push(operation);
        this.evictOldestIntermediate();

        this.currentIndex = this.states.length - 1;
        this.notifyListeners();
    }

    /**
     * Trims the stack back to {@link maxStackSize} by dropping the OLDEST
     * INTERMEDIATE state, never the baseline at {@link BASELINE_STATE_INDEX}.
     *
     * "Undo all" resolves to index 0, so index 0 has to keep
     * meaning *the state the structure was in before this session's first
     * edit*. A plain ring buffer evicts from the front, which quietly
     * reassigns that meaning to whichever edit is merely the oldest still
     * retained — and then hands the user a half-edited contour under a label
     * promising the original, with nothing on screen to reveal the swap.
     * Dropping an intermediate step instead costs undo depth, which the user
     * can see (the history list says how many steps went) and which the
     * baseline still recovers from. The baseline itself is unrecoverable once
     * dropped, so it is the one entry that is pinned — when there is one at
     * all; see {@link UndoRedoManager.hasBaseline}.
     */
    private evictOldestIntermediate(): void {
        while (this.states.length > this.maxStackSize) {
            // With no baseline to protect, index 0 is just the oldest edit and
            // there is nothing to gain by pinning it.
            const oldestEvictable = this.hasBaseline
                ? BASELINE_STATE_INDEX + 1
                : BASELINE_STATE_INDEX;
            this.states.splice(oldestEvictable, 1);
            this.discardedIntermediateCount++;
        }
    }

    /**
     * Undo - move pointer backwards and return the operation to restore to
     */
    undo(): ReversibleOperation | undefined {
        if (this.states.length === 0 || this.currentIndex <= 0) {
            return undefined;
        }
        this.currentIndex--;

        // Return the operation we're moving TO (previous state)
        const result =
            this.currentIndex >= 0 ? this.states[this.currentIndex] : undefined;
        this.notifyListeners();
        return result;
    }

    /**
     * Redo - move pointer forwards and return the operation to restore to
     */
    redo(): ReversibleOperation | undefined {
        if (this.currentIndex >= this.states.length - 1) {
            return undefined;
        }
        this.currentIndex++;

        // Return the operation we're moving TO (next state)
        const result = this.states[this.currentIndex];
        this.notifyListeners();
        return result;
    }

    /**
     * The operation one step before the current one, WITHOUT moving the undo
     * pointer — i.e. the ROI's state immediately before its most recent
     * operation. Returns undefined when there is no prior state.
     */
    peekPrevious(): ReversibleOperation | undefined {
        return this.currentIndex > 0
            ? this.states[this.currentIndex - 1]
            : undefined;
    }

    /**
     * Move the pointer to an arbitrary retained state and return the operation
     * to restore to. Rejects any index outside the retained stack rather than
     * clamping, so a caller working from a stale history list restores nothing
     * instead of restoring the wrong contour.
     *
     * Jumping does NOT drop the states it moves past: they stay reachable, in
     * both directions, until the next {@link addOperation} truncates whatever
     * is ahead of the pointer.
     */
    jumpToState(index: number): ReversibleOperation | undefined {
        if (
            !Number.isInteger(index) ||
            index < 0 ||
            index >= this.states.length
        ) {
            return undefined;
        }
        this.currentIndex = index;
        const result = this.states[index];
        this.notifyListeners();
        return result;
    }

    /**
     * Get all operations, oldest first, with current state indicator for
     * activity log and the history list.
     */
    getHistory(): UndoHistoryEntry[] {
        return this.states.map((operation, index) => ({
            index,
            operation,
            isCurrent: index === this.currentIndex,
            isBaseline: index === BASELINE_STATE_INDEX && this.hasBaseline,
        }));
    }

    /**
     * How many intermediate steps {@link evictOldestIntermediate} has dropped
     * to stay within {@link maxStackSize}. The gap they left sits between the
     * baseline and the oldest retained edit; surfacing the count is what keeps
     * the history list honest about not being the whole story.
     */
    get discardedStepCount(): number {
        return this.discardedIntermediateCount;
    }

    /** Index of the state the ROI currently reflects, or -1 when empty. */
    get currentStateIndex(): number {
        return this.currentIndex;
    }

    /**
     * Whether index 0 is a real baseline capture rather than an ordinary edit
     * that merely sorts first.
     *
     * The baseline is written by the caller on the ROI's first edit and only
     * then, so a stack built by a caller that had no pre-edit pixels to give
     * begins at an edit. Everything that treats index 0 as "the original" —
     * the history label, "Undo all", the eviction pin — asks here first,
     * rather than trusting the position.
     */
    get hasBaseline(): boolean {
        return (
            this.states[BASELINE_STATE_INDEX]?.getOperation() ===
            INITIAL_STATE_OPERATION
        );
    }

    canUndo(): boolean {
        return this.currentIndex > 0;
    }

    canRedo(): boolean {
        return this.currentIndex < this.states.length - 1;
    }

    clear(): void {
        this.states = [];
        this.currentIndex = -1;
        this.discardedIntermediateCount = 0;
        this.notifyListeners();
    }

    get size(): number {
        return this.states.length;
    }
}
